import React, { useState } from 'react';
import { Modal, Form, Select, Button, Space, Typography, Input } from 'antd';
import { useModelSelector } from '../../contexts/ModelSelectorContext';
import { UI_ROLE_OPTIONS } from '../../types/uiRoles';

const { Text } = Typography;
const { Option } = Select;

interface PinDialogProps {
  visible: boolean;
  onCancel: () => void;
  onOk: (pinData: { uiRole: string; notes?: string }) => void;
  itemName: string;
  itemType: 'artifact' | 'task';
  initialUIRole?: string;
}

export const PinDialog: React.FC<PinDialogProps> = ({
  visible,
  onCancel,
  onOk,
  itemName,
  itemType,
  initialUIRole,
}) => {
  const { uiRole: currentUIRole } = useModelSelector();
  const [form] = Form.useForm();

  const [formData, setFormData] = useState({
    uiRole: initialUIRole || currentUIRole,
    notes: '',
  });

  const handleOk = () => {
    form.validateFields().then((values) => {
      onOk(values);
      form.resetFields();
      setFormData({
        uiRole: currentUIRole,
        notes: '',
      });
    });
  };

  const handleCancel = () => {
    form.resetFields();
    setFormData({
      uiRole: currentUIRole,
      notes: '',
    });
    onCancel();
  };


  return (
    <Modal
      title={`Pin ${itemType === 'artifact' ? 'Artifact' : 'Task'}`}
      open={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      okText="Pin Item"
      cancelText="Cancel"
      width={400}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={formData}
        onValuesChange={(_, values) => setFormData(values)}
      >
        <div style={{ marginBottom: 16 }}>
          <Text strong>Pinning: {itemName}</Text>
        </div>

        <Form.Item
          name="uiRole"
          label="UI Role for Thread"
          rules={[{ required: true, message: 'Please select a UI role' }]}
        >
          <Select placeholder="Select UI role for this pinned item">
            {UI_ROLE_OPTIONS.map(option => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="notes"
          label="Notes (Optional)"
        >
          <Input.TextArea 
            placeholder="Add notes about why you're pinning this item..." 
            rows={3}
          />
        </Form.Item>

        <div style={{ 
          marginTop: 16, 
          padding: 12, 
          backgroundColor: '#f5f5f5', 
          borderRadius: 6,
          border: '1px solid #d9d9d9'
        }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            <strong>Note:</strong> This will create a new thread with the selected UI role. 
            The same {itemType} can be pinned multiple times with different UI roles.
          </Text>
        </div>
      </Form>
    </Modal>
  );
};
